import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { applyThemeRuntime } from './themeRuntime';

/** @deprecated 动效配置已迁移至 animationSettings；保留导出以兼容旧调用方。 */
export { DEFAULT_STAR_DENSITY, STAR_DENSITY_STORAGE_KEY } from '../lib/animationSettings';

export const THEME_STORAGE_KEY = 'prologue.theme';
export const CUSTOM_PALETTE_STORAGE_KEY = 'prologue.theme.custom-palette';
export const CUSTOM_RECOGNIZED_PALETTE_STORAGE_KEY = 'prologue.theme.recognized-palette';
export const SELECTED_PRIMARY_COLOR_STORAGE_KEY = 'prologue.theme.selected-primary-color';
/** @deprecated 兼容已有用户数据；新的主题状态统一使用 selectedPrimaryColor。 */
export const CUSTOM_PRIMARY_STORAGE_KEY = 'prologue.theme.custom-primary';
const CUSTOM_PRIMARY_MANUAL_STORAGE_KEY = 'prologue.theme.custom-primary-manual';
const THEME_BACKGROUND_DB = 'prologue-theme-assets';
const THEME_BACKGROUND_STORE = 'backgrounds';
const MAX_BACKGROUND_BYTES = 5 * 1024 * 1024;
export type ThemeId = 'maitian' | 'dream' | 'starlight' | 'custom';

export interface ThemePreset {
  id: ThemeId;
  name: string;
  description: string;
  swatches: [string, string, string, string?];
}

export type ThemePalette = [string, string, string];

/** 自定义主题可选主色，统一由主题设置页复用。 */
export const CUSTOM_PRIMARY_SWATCHES = [
  'var(--fp-theme-custom-primary-blue)',
  'var(--fp-theme-custom-primary-info)',
  'var(--fp-theme-custom-primary-purple)',
  'var(--fp-theme-custom-primary-cyan)',
  'var(--fp-theme-custom-primary-green)',
  'var(--fp-theme-custom-primary-amber)',
  'var(--fp-theme-custom-primary-red)',
  'var(--fp-theme-custom-primary-magenta)',
] as const;

const FALLBACK_CUSTOM_PALETTE: ThemePalette = [
  'var(--fp-theme-custom-fallback-background)',
  'var(--fp-theme-custom-fallback-surface)',
  'var(--fp-theme-custom-fallback-primary)',
];

export const THEME_PRESETS: ThemePreset[] = [
  { id: 'maitian', name: '麦田', description: '麦田金、奶油纸张与铅笔线稿，像翻开一本温柔的麦田回忆', swatches: ['var(--fp-theme-maitian-background)', 'var(--fp-theme-maitian-surface)', 'var(--fp-theme-maitian-primary)'] },
  { id: 'dream', name: '梦幻', description: '淡紫云朵、奶油白与粉色微光，营造轻盈柔软的梦境氛围', swatches: ['var(--fp-theme-dream-background)', 'var(--fp-theme-dream-surface)', 'var(--fp-theme-dream-primary)'] },
  { id: 'starlight', name: '星空', description: '蓝紫夜幕、流动星云与轨道星球，点缀清亮星光的沉浸式夜空', swatches: ['var(--fp-theme-starlight-deep)', 'var(--fp-theme-starlight-mid)', 'var(--fp-theme-starlight-primary)', 'var(--fp-theme-starlight-light)'] },
  { id: 'custom', name: '自定义（上传图片作为背景）', description: '', swatches: FALLBACK_CUSTOM_PALETTE },
];

function isThemeId(value: string | null): value is ThemeId {
  return THEME_PRESETS.some((theme) => theme.id === value);
}

export function readStoredTheme(): ThemeId {
  if (typeof window === 'undefined') return 'maitian';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isThemeId(stored) ? stored : 'maitian';
}

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
  customBackground: string | null;
  customPalette: ThemePalette | null;
  customRecognizedPalette: ThemePalette | null;
  selectedPrimaryColor: string | null;
  setCustomBackground: (file: File) => Promise<boolean>;
  setSelectedPrimaryColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function openBackgroundDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(THEME_BACKGROUND_DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(THEME_BACKGROUND_STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('主题背景存储不可用'));
  });
}

async function loadBackground(): Promise<Blob | undefined> {
  const db = await openBackgroundDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(THEME_BACKGROUND_STORE, 'readonly').objectStore(THEME_BACKGROUND_STORE).get('current');
    request.onsuccess = () => resolve(request.result as Blob | undefined);
    request.onerror = () => reject(request.error ?? new Error('读取主题背景失败'));
  });
}

async function saveBackground(file: File): Promise<void> {
  const db = await openBackgroundDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction(THEME_BACKGROUND_STORE, 'readwrite').objectStore(THEME_BACKGROUND_STORE).put(file, 'current');
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('保存主题背景失败'));
  });
}

function toHex(value: number): string {
  return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
}

function resolveCssColor(value: string): string {
  let resolved = value.trim();
  const seen = new Set<string>();
  while (resolved.startsWith('var(')) {
    const tokenName = resolved.slice(4, -1).trim();
    if (!tokenName || seen.has(tokenName)) return '';
    seen.add(tokenName);
    resolved = getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();
  }
  return resolved;
}

function readDefaultCustomPrimary(): string {
  const resolved = resolveCssColor(getComputedStyle(document.documentElement).getPropertyValue('--fp-theme-custom-primary-blue'));
  return /^#[0-9a-f]{6}$/i.test(resolved) ? resolved : resolveCssColor(FALLBACK_CUSTOM_PALETTE[2]);
}

function readStoredRecognizedPalette(): ThemePalette | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(CUSTOM_RECOGNIZED_PALETTE_STORAGE_KEY) ?? 'null');
    return Array.isArray(value) && value.length === 3 && value.every((color) => typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color))
      ? value as ThemePalette
      : null;
  } catch {
    return null;
  }
}

function readStoredSelectedPrimaryColor(): string | null {
  if (typeof window === 'undefined') return null;
  const values = [
    window.localStorage.getItem(SELECTED_PRIMARY_COLOR_STORAGE_KEY),
    window.localStorage.getItem(CUSTOM_PRIMARY_STORAGE_KEY),
  ];
  return values.find((value): value is string => Boolean(value && /^#[0-9a-f]{6}$/i.test(value))) ?? null;
}

function colorVibrancy(rgb: [number, number, number]): number {
  const values = rgb.map((channel) => channel / 255);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const saturation = max === 0 ? 0 : (max - min) / max;
  // 面积优先；仅在面积相同或接近时用色彩活跃度作为并列排序依据。
  return saturation * (0.4 + max * 0.6);
}

function colorDistance(a: [number, number, number], b: [number, number, number]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/** 从图片采样主色，并生成浅色/主色/深色三枚主题色。 */
export function extractImagePalette(blob: Blob): Promise<ThemePalette> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('图片采样不可用');
        context.drawImage(image, 0, 0, size, size);
        const pixels = context.getImageData(0, 0, size, size).data;
        const buckets = new Map<string, { rgb: [number, number, number]; count: number }>();
        for (let index = 0; index < pixels.length; index += 4) {
          if (pixels[index + 3] < 150) continue;
          const rgb: [number, number, number] = [pixels[index], pixels[index + 1], pixels[index + 2]];
          const key = rgb.map((channel) => Math.round(channel / 24)).join(',');
          const bucket = buckets.get(key);
          if (bucket) bucket.count += 1;
          else buckets.set(key, { rgb, count: 1 });
        }
        const candidates = [...buckets.values()]
          .filter(({ count }) => count >= 2)
          .sort((a, b) => b.count - a.count || colorVibrancy(b.rgb) - colorVibrancy(a.rgb));
        const selected: [number, number, number][] = [];
        for (const candidate of candidates) {
          if (selected.every((color) => colorDistance(color, candidate.rgb) > 42)) {
            selected.push(candidate.rgb);
          }
          if (selected.length === 3) break;
        }
        const fallback = candidates[0]?.rgb ?? [184, 137, 106];
        while (selected.length < 3) selected.push(fallback);
        resolve(selected.map(([red, green, blue]) => `#${toHex(red)}${toHex(green)}${toHex(blue)}`) as ThemePalette);
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(image.src);
      }
    };
    image.onerror = () => reject(new Error('读取图片颜色失败'));
    image.src = URL.createObjectURL(blob);
  });
}

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeId>(readStoredTheme);
  const [customBackground, setCustomBackgroundUrl] = useState<string | null>(null);
  const [customPalette, setCustomPalette] = useState<ThemePalette | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const value = JSON.parse(window.localStorage.getItem(CUSTOM_PALETTE_STORAGE_KEY) ?? 'null');
      const recognizedPalette = readStoredRecognizedPalette();
      const primary = window.localStorage.getItem(CUSTOM_PRIMARY_MANUAL_STORAGE_KEY) === 'true'
        ? readStoredSelectedPrimaryColor()
        : recognizedPalette?.[0] ?? null;
      const defaultPrimary = readDefaultCustomPrimary();
      return Array.isArray(value) && value.length === 3
        ? (primary && /^#[0-9a-f]{6}$/i.test(primary) ? [value[0], primary, primary] : [value[0], defaultPrimary, defaultPrimary]) as ThemePalette
        : null;
    } catch {
      return null;
    }
  });
  const [customRecognizedPalette, setCustomRecognizedPalette] = useState<ThemePalette | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return readStoredRecognizedPalette();
    } catch {
      return null;
    }
  });
  const [selectedPrimaryColor, setSelectedPrimaryColorState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    if (window.localStorage.getItem(CUSTOM_PRIMARY_MANUAL_STORAGE_KEY) === 'true') return readStoredSelectedPrimaryColor();
    return readStoredRecognizedPalette()?.[0] ?? readStoredSelectedPrimaryColor();
  });

  useEffect(() => {
    let objectUrl: string | null = null;
    void loadBackground().then((blob) => {
      if (!blob) return;
      objectUrl = URL.createObjectURL(blob);
      setCustomBackgroundUrl(objectUrl);
      void extractImagePalette(blob).then((recognizedPalette) => {
        setCustomRecognizedPalette(recognizedPalette);
        setCustomPalette((previous) => {
          const manual = window.localStorage.getItem(CUSTOM_PRIMARY_MANUAL_STORAGE_KEY) === 'true';
          const primary = manual ? readStoredSelectedPrimaryColor() ?? previous?.[1] ?? readDefaultCustomPrimary() : recognizedPalette[0];
          return [recognizedPalette[0], primary, primary];
        });
      }).catch(() => undefined);
    }).catch(() => undefined);
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const setCustomBackground = async (file: File): Promise<boolean> => {
    if (!file.type.startsWith('image/')) return false;
    if (file.size > MAX_BACKGROUND_BYTES) return false;
    const palette = await extractImagePalette(file);
    const primary = selectedPrimaryColor ?? palette[0];
    const nextPalette: ThemePalette = [palette[0], primary, primary];
    await saveBackground(file);
    window.localStorage.setItem(CUSTOM_PALETTE_STORAGE_KEY, JSON.stringify(nextPalette));
    window.localStorage.setItem(CUSTOM_RECOGNIZED_PALETTE_STORAGE_KEY, JSON.stringify(palette));
    const nextUrl = URL.createObjectURL(file);
    setCustomBackgroundUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return nextUrl;
    });
    setCustomPalette(nextPalette);
    setCustomRecognizedPalette(palette);
    setTheme('custom');
    return true;
  };

  const setSelectedPrimaryColor = (color: string): void => {
    const resolvedColor = resolveCssColor(color);
    if (!/^#[0-9a-f]{6}$/i.test(resolvedColor)) return;
    setSelectedPrimaryColorState(resolvedColor);
    window.localStorage.setItem(SELECTED_PRIMARY_COLOR_STORAGE_KEY, resolvedColor);
    window.localStorage.setItem(CUSTOM_PRIMARY_STORAGE_KEY, resolvedColor);
    window.localStorage.setItem(CUSTOM_PRIMARY_MANUAL_STORAGE_KEY, 'true');
    setCustomPalette((previous) => previous ? [previous[0], resolvedColor, resolvedColor] : [resolveCssColor(FALLBACK_CUSTOM_PALETTE[0]), resolvedColor, resolvedColor]);
    setTheme('custom');
  };

  useEffect(() => {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    applyThemeRuntime(theme, customPalette, customBackground);
  }, [theme, customBackground, customPalette]);

  const value = useMemo(() => ({ theme, setTheme, customBackground, customPalette, customRecognizedPalette, selectedPrimaryColor, setCustomBackground, setSelectedPrimaryColor }), [theme, customBackground, customPalette, customRecognizedPalette, selectedPrimaryColor]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme 必须在 ThemeProvider 内使用');
  return context;
}

/** 共享资源图标等可降级组件使用；无 ThemeProvider 时返回 null。 */
export function useThemeOptional(): ThemeContextValue | null {
  return useContext(ThemeContext);
}
